import { DialogCheckBox } from 'components/DialogCheckBox';
import { DialogConfirmation } from 'components/DialogConfirmation';
import { Snackbar } from 'components/Snackbar';
import { IUseDisclose, useDisclose, useLoader } from 'hooks/utilHooks';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ManagementService } from 'services';
import { IManagementCreate, IManagementList } from 'types/Management';

import { AlertColor } from '@mui/material';

export interface IManagementContext {
  getManagers: Function;
  getManagersCb: Function;
  handleOnSubmit: Function;
  setManagerId: Function;
  managers: IManagementList;
  deleteDialog: IUseDisclose;
  loadingDelete: boolean;
  loadingList: boolean;
  loadingCreate: boolean;
}

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
}

export const ManagementContext = createContext<IManagementContext>(
  {} as IManagementContext
);

type Props = {
  children: ReactNode;
};

export const ManagementProvider = ({ children }: Props) => {
  const [managers, setManagers] = useState<IManagementList>({
    companyUsers: [],
  });

  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });

  const loadingList = useLoader(true);
  const loadingCreate = useLoader();
  const loadingDelete = useLoader();

  const deleteDialog = useDisclose();
  const successModal = useDisclose();

  const [managerId, setManagerId] = useState<string>('');

  const getManagers = useCallback(async () => {
    try {
      const managers = await ManagementService.getResponsibleRHList();
      return managers;
    } catch (error) {
      setSnackbar({
        message: 'Não foi possível listar os colaboradores',
        variant: 'error',
      });
      return { companyUsers: [] };
    }
  }, []);

  const getManagersCb = useCallback(
    async (force = false) => {
      if (force === true) {
        const managers = await getManagers();
        setManagers(managers);
      }
    },

    [getManagers]
  );

  const handleOnSubmit = async (
    values: IManagementCreate & { id?: string }
  ) => {
    try {
      loadingCreate.showLoader();

      if (managers.companyUsers.length === 5) {
        return;
      }

      await ManagementService.createResponsibleRH(values);

      setSnackbar({
        message: 'Responsável do RH cadastrado com sucesso!',
        variant: 'success',
      });

      getManagersCb(true);
      return true;
    } catch (error) {
      const { message } = error as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
      return false;
    } finally {
      loadingCreate.hideLoader();
    }
  };

  const deleteManager = async () => {
    try {
      loadingDelete.showLoader();
      await ManagementService.deleteResponsibleRHById(managerId);

      deleteDialog.onClose();
      successModal.onOpen();

      getManagersCb(true);

      setSnackbar({
        message: 'Responsável do RH apagado com sucesso',
        variant: 'success',
      });
    } catch (error) {
      setSnackbar({
        message: 'Não foi possível apagar o Responsável do RH',
        variant: 'error',
      });
    } finally {
      loadingDelete.hideLoader();
    }
  };

  useEffect(() => {
    getManagers().then((managers: IManagementList) => {
      loadingList.hideLoader();
      setManagers(managers);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setManagers, getManagers]);

  useEffect(() => {
    getManagersCb();
  }, [getManagersCb]);

  return (
    <ManagementContext.Provider
      value={{
        getManagers,
        getManagersCb,
        handleOnSubmit,

        setManagerId,
        managers,
        deleteDialog,
        loadingList: loadingList.loading,
        loadingCreate: loadingCreate.loading,
        loadingDelete: loadingDelete.loading,
      }}
    >
      {children}

      <Snackbar
        text={snackbar.message}
        snackbarOpen={Boolean(snackbar.message)}
        setSnackbarOpen={() => {
          setSnackbar({
            message: '',
          });
        }}
        severityType={snackbar.variant}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />

      <DialogCheckBox
        title="Exclusão permanente"
        text="Essa ação é permanente, você deseja continuar e excluir todos os
        dados dessa empresa?"
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        loadingDelete={loadingDelete.loading}
        deleteItem={deleteManager}
      />
      <DialogConfirmation
        title="Exclusão permantente do colaborador"
        text="Exclusão realizada com sucesso!"
        isOpen={successModal.isOpen}
        onClose={successModal.onClose}
      />
    </ManagementContext.Provider>
  );
};
